<template>
  <div class="tabs-container" :class="{ ['pill-styled']: pill }">
    <div class="tabs d-flex position-relative" ref="tabContainer">
      <div
        :ref="'tab-' + index"
        @click="$emit('change', index)"
        class="tab text-normal pointer"
        v-for="(item, index) in items"
        :key="item.id || item"
        :class="{
          active: index === Number(selectedIndex),
          [`mx-${tabSpacing}`]: !pill,
          ['px-2']: pill,
          [tabClass]: true
        }"
      >
        <template v-if="$scopedSlots.default">
          <slot v-bind:item="item"> </slot>
        </template>
        <template v-else>
          {{ item.name || item }}
        </template>
      </div>
      <div
        class="indicator position-absolute"
        :class="{ [variant]: true }"
        :style="{ left: indicatorLeft, width: indicatorWidth }"
        v-if="showSlider"
      ></div>
    </div>
  </div>
</template>

<script>
/**
 * This component gives navigation tabs.
 * It supports two styling
 * - default: In default styling, the selected tab has a thin border at the bottom which slides to next item upon selection
 * - pill: This can be used by passing prop - pill as true. In this, the selected tab has a blue background which slides to next item upon selection
 */
export default {
  name: 'Tabs',
  props: {
    items: Array,
    selectedIndex: Number,
    showSlider: Boolean,
    pill: Boolean,
    /**
     * Supported values: primary, secondary
     */
    variant: {
      default: 'primary'
    },
    tabClass: {
      default: ''
    },
    /**
     * This is spacing between tabs. Can be 1,2,3,4,5. Greater the number, more the spacing.
     */
    tabSpacing: {
      default: 2,
      type: Number
    }
  },
  model: {
    prop: 'selectedIndex',
    event: 'change'
  },
  data() {
    return {
      indicatorLeft: null,
      indicatorWidth: null
    }
  },
  methods: {
    updateIndicator() {
      const selectedTabRef = this.$refs['tab-' + this.selectedIndex]
      if (!selectedTabRef) return
      const selectedTab = selectedTabRef[0]
      const { left: containerLeft } = this.$refs.tabContainer.getBoundingClientRect()
      const { left, width } = selectedTab.getBoundingClientRect()
      this.indicatorLeft = `${left - containerLeft}px`
      this.indicatorWidth = `${width}px`
    }
  },
  watch: {
    selectedIndex() {
      this.updateIndicator()
      setTimeout(this.updateIndicator, 0)
    }
  },
  mounted() {
    this.updateIndicator()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.indicator {
  position: absolute;
  top: calc(100% - 2px);
  min-width: 0;
  width: 0;
  height: 2px;
  background: $brand;
  transition: left ease-out 0.2s, width ease-out 0.2s;
  &.secondary {
    background: $prime;
  }
}

.tabs-container {
  white-space: nowrap;
  border-radius: 40px;
}

.tab {
  padding: 10px 0;
  color: $secondary;

  &.active {
    color: $primary;
    font-weight: 600;
  }
}
.pill-styled {
  .tab {
    padding: 2px 0;
    transition: color 300ms ease-in;
    z-index: 1;
    &.active {
      &,
      * {
        color: $documentColor;
        font-weight: normal;
      }
    }
  }
  .indicator {
    height: auto;
    top: 0;
    bottom: 0;
    border-radius: 50px;
  }
}
</style>
